// Global Instructions Rule Applied!

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Form, Input, Select, Card, Row, Col, Spin, Typography, Space, Button } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { store } from '../../../../state_manager/store.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { removeTab, updateTabTitle } from '../../../../store/slices/tabSlice'
import type {
  DistributionListGridModel,
  DistributionListFilterOptionsModel,
  DistributionListFilterOption
} from '../../../../state_manager/data_models/distribution_list_grid_model.ts'
import type { DistributionListSavePayload } from '../../../../state_manager/interfaces/distribution_lists.ts'
import {
  fetchDistributionListDetail,
  fetchDistributionListFilterOptions,
  createDistributionList,
  updateDistributionList
} from '../../services/distributionListsService.ts'
import { renderContactOption, contactOptionFilter } from '../contactOptionRender.tsx'

const { Title } = Typography

interface FullDistributionListFormProps {
  recordId?: string | null
  isNew?: boolean
}

interface ListFormValues {
  name: string
  topic_id: string
  account_role_choice_ids: string[]
  fund_ids: string[]
  comms_lp_status_ids: string[]
  account_ids: string[]
  contact_ids: string[]
}

const LISTS_PATH = '/distribution-lists'

export default function FullDistributionListForm({
  recordId: propRecordId,
  isNew: propIsNew
}: FullDistributionListFormProps) {
  const { urlRecordId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [form] = Form.useForm<ListFormValues>()

  const recordId = propRecordId !== undefined ? propRecordId : urlRecordId ?? null
  const isNew = propIsNew !== undefined ? propIsNew : recordId === 'new' || !recordId

  const formTabKey = `distribution-lists-form-${isNew ? 'new' : 'edit'}-${recordId || 'new'}`

  const [options, setOptions] = useState<DistributionListFilterOptionsModel | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [notFound, setNotFound] = useState(false)

  const topicOptions = useMemo(() => options?.topic ?? [], [options])
  const roleOptions = useMemo(() => options?.roles ?? [], [options])
  const fundOptions = useMemo(() => options?.funds ?? [], [options])
  const lpStatusOptions = useMemo(() => options?.lp_status ?? [], [options])
  const accountOptions = useMemo(() => options?.accounts ?? [], [options])
  const contactOptions = useMemo(() => options?.contacts ?? [], [options])

  // Filter-options once on mount (extended Phase 0 RPC; preloaded, the AntD
  // multiselects filter client-side — decision §2.13d).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchDistributionListFilterOptions(token)
      if (cancelled) return
      if (res.error) showErrorToast(res.error)
      setOptions(res.data ?? null)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Cold-load detail: the screen is screen-local (no Redux prefetch — §2.12e)
  // so the form ALWAYS fetches its own record. This makes the CLAUDE.md
  // hard-refresh-form path the normal path.
  useEffect(() => {
    let cancelled = false
    if (isNew) {
      dispatch(updateTabTitle({ key: formTabKey, title: 'New Distribution List' }))
      return
    }
    ;(async () => {
      setLoading(true)
      const token = getAccessToken(store.getState().auth)
      const res = await fetchDistributionListDetail(String(recordId), token)
      if (cancelled) return
      if (res.error) {
        showErrorToast(res.error)
        setLoading(false)
        return
      }
      const d: DistributionListGridModel | null = res.data
      if (!d) {
        setNotFound(true)
        setLoading(false)
        return
      }
      form.setFieldsValue({
        name: d.distribution_lists__name,
        topic_id: d.distribution_lists__topic_id,
        account_role_choice_ids: d.distribution_lists__role_ids ?? [],
        fund_ids: d.distribution_lists__fund_ids ?? [],
        comms_lp_status_ids: d.distribution_lists__lp_status_ids ?? [],
        account_ids: d.distribution_lists__account_ids ?? [],
        contact_ids: d.distribution_lists__contact_ids ?? []
      })
      dispatch(
        updateTabTitle({ key: formTabKey, title: `Edit ${d.distribution_lists__name}` })
      )
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [recordId, isNew, form, dispatch, formTabKey])

  const closeToList = useCallback(() => {
    navigate(LISTS_PATH)
    dispatch(removeTab({ key: formTabKey }))
  }, [navigate, dispatch, formTabKey])

  const handleSave = useCallback(async () => {
    let values: ListFormValues
    try {
      values = await form.validateFields()
    } catch {
      return // AntD shows field errors
    }
    setSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const payload: DistributionListSavePayload = {
        name: values.name.trim(),
        topic_id: values.topic_id,
        account_role_choice_ids: values.account_role_choice_ids ?? [],
        fund_ids: values.fund_ids ?? [],
        comms_lp_status_ids: values.comms_lp_status_ids ?? [],
        account_ids: values.account_ids ?? [],
        contact_ids: values.contact_ids ?? []
      }
      const result =
        isNew || !recordId
          ? await createDistributionList(payload, token)
          : await updateDistributionList(String(recordId), payload, token)

      if (result.error || !result.data) {
        showErrorToast(result.error || 'Failed to save distribution list')
        return
      }
      toast.success(isNew ? 'Distribution list created' : 'Distribution list updated')
      closeToList()
    } finally {
      setSaving(false)
    }
  }, [form, isNew, recordId, closeToList])

  return (
    <div className='h-full overflow-auto bg-white'>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={closeToList} />
          <Title level={4} style={{ margin: 0 }}>
            {isNew ? 'New Distribution List' : 'Edit Distribution List'}
          </Title>
        </Space>
        <Space>
          <Button onClick={closeToList}>Cancel</Button>
          <Button type='primary' loading={saving} onClick={handleSave} disabled={notFound}>
            {isNew ? 'Create' : 'Save'}
          </Button>
        </Space>
      </div>

      {notFound ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
          This distribution list was not found (it may have been deleted).
        </div>
      ) : (
        <Spin spinning={loading}>
          <div style={{ padding: 24, maxWidth: 1180, margin: '0 auto' }}>
            <Form form={form} layout='vertical' autoComplete='off'>
              <Row gutter={20}>
                <Col xs={24} md={12}>
                  <Card title='Definition' style={{ marginBottom: 20 }}>
                    <Form.Item
                      name='name'
                      label='Name'
                      rules={[
                        { required: true, message: 'Distribution list name is required' },
                        { whitespace: true, message: 'Distribution list name is required' }
                      ]}
                    >
                      <Input placeholder='e.g. Q1 LP Reporting' maxLength={200} allowClear />
                    </Form.Item>
                    <Form.Item
                      name='topic_id'
                      label='Topic'
                      rules={[{ required: true, message: 'Topic is required' }]}
                      tooltip='Recipients are resolved from accounts/contacts subscribed to this topic.'
                    >
                      <Select
                        allowClear
                        placeholder='Select a topic'
                        options={topicOptions}
                        optionFilterProp='label'
                        showSearch
                      />
                    </Form.Item>
                  </Card>
                </Col>
                <Col xs={24} md={12}>
                  <Card title='Filters' style={{ marginBottom: 20 }}>
                    <Form.Item
                      name='account_role_choice_ids'
                      label='Account roles'
                      tooltip='Optional. Empty = no account-role filter on this list.'
                    >
                      <Select
                        mode='multiple'
                        allowClear
                        placeholder='Any account role'
                        options={roleOptions}
                        optionFilterProp='label'
                        showSearch
                      />
                    </Form.Item>
                    <Form.Item
                      name='fund_ids'
                      label='Funds'
                      tooltip='Optional. Empty = no fund filter on this list.'
                    >
                      <Select
                        mode='multiple'
                        allowClear
                        placeholder='Any fund'
                        options={fundOptions}
                        optionFilterProp='label'
                        showSearch
                      />
                    </Form.Item>
                    <Form.Item
                      name='comms_lp_status_ids'
                      label='LP statuses'
                      tooltip='Optional. Empty = no LP-status filter on this list.'
                    >
                      <Select
                        mode='multiple'
                        allowClear
                        placeholder='Any LP status'
                        options={lpStatusOptions}
                        optionFilterProp='label'
                        showSearch
                      />
                    </Form.Item>
                  </Card>
                </Col>
                <Col xs={24}>
                  <Card title='Always include — force-include overlay'>
                    <Row gutter={20}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name='account_ids'
                          label='Specific accounts'
                          tooltip='Force-included on top of the topic/role/fund/LP-status resolution.'
                        >
                          <Select
                            mode='multiple'
                            allowClear
                            placeholder='Search accounts to always include'
                            options={accountOptions}
                            optionFilterProp='label'
                            showSearch
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name='contact_ids'
                          label='Specific contacts'
                          tooltip='Force-included on top of the topic/role/fund/LP-status resolution. Search by name or email.'
                        >
                          <Select
                            mode='multiple'
                            allowClear
                            placeholder='Search contacts to always include'
                            options={contactOptions}
                            optionRender={(oi) =>
                              renderContactOption(oi.data as DistributionListFilterOption)
                            }
                            filterOption={(input, opt) =>
                              contactOptionFilter(input, opt as DistributionListFilterOption | undefined)
                            }
                            showSearch
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            </Form>
          </div>
        </Spin>
      )}
    </div>
  )
}
